/*
 * Variant Barcode Media Filter (VBMF)
 * Filters the product media gallery to the images whose alt text contains the
 * selected variant's barcode (or the colour-group's barcodes via option1).
 *
 * Reviewed/optimised version:
 *  - Robust variant-change detection (theme pub/sub + document + section + input).
 *  - Re-queries the gallery DOM on each apply so late-injected/reordered media
 *    (e.g. Dawn's updateMedia prepend) are handled, not just init-time nodes.
 *  - Keeps the active image eager for LCP; lazy/unloads the hidden ones.
 *  - Updates the slider counter total to the visible count.
 *  - Also filters the fullscreen media modal so it matches the inline gallery.
 *  - No global window 'resize' dispatch (uses slider resetPages only).
 */
(function () {
  const cssEscape =
    window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape
      : (value) => String(value);

  function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Barcode must be a whole token within the alt text (no partial/substring hits).
  function matchesBarcode(alt, barcode) {
    if (!alt || !barcode) return false;
    const altNorm = String(alt).toLowerCase();
    const codeNorm = String(barcode).trim().toLowerCase();
    if (!altNorm || !codeNorm) return false;
    const pattern = new RegExp('(^|[^a-z0-9])' + escapeRegExp(codeNorm) + '([^a-z0-9]|$)', 'i');
    return pattern.test(altNorm);
  }

  function matchesAnyBarcode(alt, barcodes) {
    if (!alt || !barcodes || !barcodes.length) return false;
    for (let i = 0; i < barcodes.length; i++) {
      if (matchesBarcode(alt, barcodes[i])) return true;
    }
    return false;
  }

  function getUrlVariantId() {
    try {
      const v = new URLSearchParams(window.location.search).get('variant');
      return v ? String(v) : null;
    } catch (e) {
      return null;
    }
  }

  function throttle(fn, wait) {
    let last = 0,
      timer = null,
      lastArgs = null,
      lastThis = null;
    return function throttled(...args) {
      const now = Date.now();
      const remaining = wait - (now - last);
      lastArgs = args;
      lastThis = this;
      if (remaining <= 0) {
        last = now;
        fn.apply(lastThis, lastArgs);
        lastArgs = lastThis = null;
      } else if (!timer) {
        timer = setTimeout(() => {
          timer = null;
          last = Date.now();
          fn.apply(lastThis, lastArgs);
          lastArgs = lastThis = null;
        }, remaining);
      }
    };
  }

  if (!window.__vbmfBoundSections) window.__vbmfBoundSections = new WeakSet();

  class VariantBarcodeMediaFilter {
    constructor(gallery) {
      this.gallery = gallery;
      this.viewer = gallery.querySelector('[id^="GalleryViewer"]');
      this.thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!this.viewer) return;
      this.section =
        this.gallery.closest('[id^="MainProduct-"]') ||
        this.gallery.closest('[data-section-id]') ||
        this.gallery.closest('section');

      const variantData = this.readVariantData();
      this.variants = variantData.variants;
      this.barcodeById = variantData.barcodeById;

      this.refreshDom();
      this.isApplying = false;
      this.lastAppliedKey = null;
      this.refreshThumbnailSlider = throttle(this._refreshThumbnailSlider.bind(this), 120);

      this.bindVariantChange();
      this.applyInitial();
    }

    // (#4/#5) Re-read the current gallery/modal DOM so reordered or
    // late-injected media are always included, then make sure new <img> have
    // their src cached for load/unload management.
    refreshDom() {
      this.allMain = Array.from(this.viewer.querySelectorAll('[data-media-id]'));
      this.allThumbs = this.thumbnails
        ? Array.from(this.thumbnails.querySelectorAll('[data-target]'))
        : [];
      this.modal = this.section
        ? this.section.querySelector('[id^="ProductModal-"], product-modal, .product-media-modal')
        : null;
      this.allModal = this.modal
        ? Array.from(this.modal.querySelectorAll('[data-media-id]'))
        : [];
      this.cacheImageSources();
    }

    readVariantData() {
      const result = { variants: [], barcodeById: new Map() };
      if (!this.section) return result;
      const script = this.section.querySelector(
        'script[type="application/json"][data-variant-barcodes]'
      );
      if (!script) return result;
      try {
        const arr = JSON.parse(script.textContent.trim());
        if (Array.isArray(arr)) {
          arr.forEach((v) => {
            if (!v || !v.id) return;
            const id = String(v.id);
            const rec = {
              id,
              barcode: v.barcode ? String(v.barcode) : null,
              option1: v.option1 !== undefined && v.option1 !== null ? String(v.option1) : null,
            };
            result.variants.push(rec);
            if (rec.barcode) result.barcodeById.set(id, rec.barcode);
          });
        }
      } catch (e) {
        console.warn('[VBMF] Failed to parse data-variant-barcodes JSON', e);
      }
      return result;
    }

    getVariantRecord(id) {
      if (!id || !this.variants.length) return null;
      const vid = String(id);
      return this.variants.find((v) => v.id === vid) || null;
    }

    getBarcodeFromId(id) {
      if (!id) return null;
      return this.barcodeById.get(String(id)) || null;
    }

    getCurrentVariantIdFromForm() {
      if (!this.section) return null;
      const idInput = this.section.querySelector(
        'form[action*="/cart/add"] [name="id"], product-form form [name="id"]'
      );
      return idInput && idInput.value ? String(idInput.value) : null;
    }

    cacheImageSources() {
      const containers = [...this.allMain, ...this.allThumbs, ...this.allModal];
      containers.forEach((container) => {
        if (!container) return;
        container.querySelectorAll('img').forEach((img) => {
          if (img.dataset.barcodeManaged === 'true') return;
          const src = img.getAttribute('src');
          const srcset = img.getAttribute('srcset');
          const sizes = img.getAttribute('sizes');
          if (src) img.dataset.barcodeSrc = src;
          if (srcset) img.dataset.barcodeSrcset = srcset;
          if (sizes) img.dataset.barcodeSizes = sizes;
          img.dataset.barcodeManaged = 'true';
        });
      });
    }

    loadMedia(container, eager) {
      if (!container) return;
      container.querySelectorAll('img').forEach((img) => {
        if (img.dataset.barcodeManaged !== 'true') return;
        if (!img.getAttribute('src') && img.dataset.barcodeSrc)
          img.setAttribute('src', img.dataset.barcodeSrc);
        if (!img.getAttribute('srcset') && img.dataset.barcodeSrcset)
          img.setAttribute('srcset', img.dataset.barcodeSrcset);
        if (!img.getAttribute('sizes') && img.dataset.barcodeSizes)
          img.setAttribute('sizes', img.dataset.barcodeSizes);
        // (#10) keep the active media eager for LCP, lazy-load the rest.
        if (eager) {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
        } else {
          img.setAttribute('loading', 'lazy');
        }
      });
    }

    unloadMedia(container) {
      if (!container) return;
      container.querySelectorAll('img').forEach((img) => {
        if (img.dataset.barcodeManaged !== 'true') return;
        if (img.getAttribute('src')) img.removeAttribute('src');
        if (img.getAttribute('srcset')) img.removeAttribute('srcset');
        if (img.getAttribute('sizes')) img.removeAttribute('sizes');
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      });
    }

    getGroupBarcodes(variantId) {
      const rec = this.getVariantRecord(variantId);
      if (!rec || !rec.option1) return [];
      const color = rec.option1;
      const codes = new Set();
      this.variants.forEach((v) => {
        if (v.option1 === color && v.barcode) codes.add(v.barcode);
      });
      return Array.from(codes);
    }

    hasAnyMatchForBarcode(barcode) {
      if (!barcode) return false;
      for (let i = 0; i < this.allMain.length; i++) {
        const img = this.allMain[i].querySelector('img');
        const alt = img && img.alt ? img.alt : '';
        if (matchesBarcode(alt, barcode)) return true;
      }
      return false;
    }

    filterBarcodesWithImages(barcodes) {
      const out = [];
      if (!barcodes) return out;
      barcodes.forEach((b) => {
        if (b && this.hasAnyMatchForBarcode(b)) out.push(b);
      });
      return out;
    }

    getFallbackMainMediaForVariant(variantId) {
      if (!this.allMain.length) return null;
      const vid = variantId ? String(variantId) : null;
      if (vid) {
        const candidate = this.allMain.find(
          (item) => item.dataset.variantId === vid || item.dataset.mediaVariantId === vid
        );
        if (candidate) return candidate;
        const barcode = this.getBarcodeFromId(vid);
        if (barcode) {
          const byBarcode = this.allMain.find((item) => {
            const img = item.querySelector('img');
            const alt = img && img.alt ? img.alt : '';
            return matchesBarcode(alt, barcode);
          });
          if (byBarcode) return byBarcode;
        }
      }
      return this.allMain[0];
    }

    showVariantMainOnly(variantId) {
      const mainMedia = this.getFallbackMainMediaForVariant(variantId);
      if (!mainMedia) return;
      const mainMediaId = mainMedia.dataset.mediaId || mainMedia.getAttribute('data-media-id');
      this.allMain.forEach((item) => this.toggleItem(item, item === mainMedia, item === mainMedia));
      if (this.thumbnails) {
        this.allThumbs.forEach((thumb) =>
          this.toggleItem(thumb, mainMediaId && thumb.dataset.target === String(mainMediaId))
        );
      }
      this.allModal.forEach((item) =>
        this.toggleItem(
          item,
          mainMediaId && (item.dataset.mediaId || item.getAttribute('data-media-id')) === String(mainMediaId)
        )
      );
      this.updateSliderCounter();
      this.refreshThumbnailSlider();
      this.ensureActive();
    }

    bindVariantChange() {
      if (!this.section) return;
      const handle = (variant) => {
        Promise.resolve().then(() => {
          let variantId = variant && variant.id ? String(variant.id) : null;
          let barcode = variant && variant.barcode ? String(variant.barcode) : null;
          if (!variantId) variantId = getUrlVariantId() || this.getCurrentVariantIdFromForm();
          if (!barcode && variantId) barcode = this.getBarcodeFromId(variantId);
          this.filterForVariant(variantId, barcode);
        });
      };

      // (#3) Theme pub/sub is the reliable signal product-info.js publishes.
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        try {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
            const v = event && event.data && event.data.variant;
            // Only react to this section's variant change when sectionId is provided.
            if (event && event.data && event.data.sectionId && this.section && this.section.dataset) {
              const sid = this.section.dataset.sectionId || this.section.id;
              if (sid && String(event.data.sectionId) !== String(sid).replace(/^MainProduct-/, '')) {
                // still fall through; filtering is idempotent via lastAppliedKey
              }
            }
            handle(v);
          });
        } catch (e) {}
      }
      // (#3) document-level CustomEvent dispatched by product-variant-options.
      document.addEventListener('variant:change', (event) =>
        handle(event.detail && event.detail.variant)
      );

      // Section-scoped + legacy listeners (kept for safety; bound once per section).
      if (!window.__vbmfBoundSections.has(this.section)) {
        window.__vbmfBoundSections.add(this.section);
        this.section.addEventListener('variant:changed', (event) =>
          handle(event.detail && event.detail.variant)
        );
        this.section.addEventListener('variant:change', (event) =>
          handle(event.detail && event.detail.variant)
        );
        const idInput = this.section.querySelector(
          'form[action*="/cart/add"] [name="id"], product-form form [name="id"]'
        );
        if (idInput) {
          idInput.addEventListener('change', () => {
            const variantId = String(idInput.value || '');
            this.filterForVariant(variantId, this.getBarcodeFromId(variantId));
          });
        }
      }
    }

    applyInitial() {
      const initialId = getUrlVariantId() || this.getCurrentVariantIdFromForm();
      this.filterForVariant(initialId, this.getBarcodeFromId(initialId));
    }

    filterForVariant(variantId, activeBarcode) {
      if (this.isApplying) return;
      const key = `${variantId || 'noid'}|${activeBarcode || 'nobar'}`;
      if (this.lastAppliedKey === key) return;
      this.isApplying = true;
      try {
        this.refreshDom(); // (#4/#5) pick up reordered / newly-added media
        if (activeBarcode && this.hasAnyMatchForBarcode(activeBarcode)) {
          this.applyBarcodeSetFilter([activeBarcode], variantId);
          this.lastAppliedKey = key;
          return;
        }
        if (variantId) {
          const groupUsable = this.filterBarcodesWithImages(this.getGroupBarcodes(variantId));
          if (groupUsable.length) {
            this.applyBarcodeSetFilter(groupUsable, variantId);
            this.lastAppliedKey = key;
            return;
          }
        }
        if (variantId) this.showVariantMainOnly(variantId);
        else this.showAll();
        this.lastAppliedKey = key;
      } catch (e) {
        console.warn('[VBMF] filterForVariant error', e);
        if (variantId) this.showVariantMainOnly(variantId);
        else this.showAll();
        this.lastAppliedKey = key + '|err';
      } finally {
        this.isApplying = false;
      }
    }

    applyBarcodeSetFilter(barcodes, variantId) {
      let hasMatch = false;
      this.allMain.forEach((item) => {
        const img = item.querySelector('img');
        const alt = img && img.alt ? img.alt : '';
        const match = matchesAnyBarcode(alt, barcodes);
        this.toggleItem(item, match);
        if (match) hasMatch = true;
      });
      this.allThumbs.forEach((thumb) => {
        const targetId = thumb.dataset.target;
        let alt = '';
        const thumbImg = thumb.querySelector('img');
        if (thumbImg && thumbImg.alt) alt = thumbImg.alt;
        else if (targetId) {
          const mainImg = this.viewer.querySelector(`[data-media-id="${cssEscape(targetId)}"] img`);
          if (mainImg && mainImg.alt) alt = mainImg.alt;
        }
        this.toggleItem(thumb, matchesAnyBarcode(alt, barcodes));
      });
      // (#6) keep the fullscreen modal in sync with the inline gallery
      this.allModal.forEach((item) => {
        const img = item.querySelector('img');
        const alt = img && img.alt ? img.alt : '';
        this.toggleItem(item, matchesAnyBarcode(alt, barcodes));
      });
      if (!hasMatch) {
        if (variantId) this.showVariantMainOnly(variantId);
        else this.showAll();
        return;
      }
      this.updateSliderCounter();
      this.refreshThumbnailSlider();
      this.ensureActive();
    }

    toggleItem(el, shouldShow, eager) {
      if (!el) return;
      if (shouldShow) {
        if (el.style.display === 'none') el.style.display = '';
        el.classList.remove('is-hidden-by-barcode');
        this.loadMedia(el, !!eager);
      } else {
        if (el.style.display !== 'none') el.style.display = 'none';
        el.classList.add('is-hidden-by-barcode');
        this.unloadMedia(el);
      }
    }

    showAll() {
      this.allMain.forEach((item, i) => {
        if (item.style.display === 'none') item.style.display = '';
        item.classList.remove('is-hidden-by-barcode');
        this.loadMedia(item, i === 0);
      });
      this.allThumbs.forEach((thumb) => {
        if (thumb.style.display === 'none') thumb.style.display = '';
        thumb.classList.remove('is-hidden-by-barcode');
        this.loadMedia(thumb);
      });
      this.allModal.forEach((item) => {
        if (item.style.display === 'none') item.style.display = '';
        item.classList.remove('is-hidden-by-barcode');
        this.loadMedia(item);
      });
      this.updateSliderCounter();
      this.refreshThumbnailSlider();
      this.ensureActive();
    }

    ensureActive() {
      const visible = this.allMain.filter((item) => item.style.display !== 'none');
      if (!visible.length) return;
      let active = visible.find((item) => item.classList.contains('is-active')) || visible[0];
      const mediaId = active.dataset.mediaId || active.getAttribute('data-media-id');
      if (!mediaId) return;
      // make sure the active image is eager for LCP
      this.loadMedia(active, true);
      const alreadyActive = active.classList.contains('is-active');
      if (typeof this.gallery.setActiveMedia === 'function') {
        if (!alreadyActive) this.gallery.setActiveMedia(mediaId, false);
      } else if (!alreadyActive) {
        this.allMain.forEach((el) => el.classList.remove('is-active'));
        active.classList.add('is-active');
        if (this.thumbnails) {
          const thumb = this.thumbnails.querySelector(`[data-target="${cssEscape(mediaId)}"]`);
          if (thumb) {
            this.thumbnails
              .querySelectorAll('button[aria-current="true"]')
              .forEach((btn) => btn.removeAttribute('aria-current'));
            const btn = thumb.querySelector('button');
            if (btn) btn.setAttribute('aria-current', 'true');
          }
        }
      }
    }

    // (#7) reflect the number of *visible* slides in the slider counter total
    updateSliderCounter() {
      if (!this.viewer) return;
      const total = this.allMain.filter((item) => item.style.display !== 'none').length;
      const totalEl = this.viewer.querySelector('.slider-counter--total');
      if (totalEl) totalEl.textContent = total;
      const currentEl = this.viewer.querySelector('.slider-counter--current');
      if (currentEl) currentEl.textContent = total > 0 ? 1 : 0;
    }

    _refreshThumbnailSlider() {
      if (!this.thumbnails) return;
      const sliderComponent = this.thumbnails.closest('slider-component');
      if (!sliderComponent) return;
      // (#11) only use the component's own reset; no global resize dispatch.
      try {
        if (typeof sliderComponent.resetPages === 'function') sliderComponent.resetPages();
      } catch (e) {}
    }
  }

  function initVariantBarcodeMediaFilters() {
    if (!window.customElements) return;
    const start = () =>
      document
        .querySelectorAll('media-gallery')
        .forEach((gallery) => new VariantBarcodeMediaFilter(gallery));
    if (!customElements.get('media-gallery') && customElements.whenDefined) {
      customElements.whenDefined('media-gallery').then(start);
    } else {
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantBarcodeMediaFilters);
  } else {
    initVariantBarcodeMediaFilters();
  }
})();
